import type { Photo, User, Visibility } from './model';

/** The user fields that decide what someone can do. */
export type AccessUser = Pick<User, 'id' | 'status' | 'isAdmin' | 'type' | 'canRelease' | 'canSeeRestricted'>;

/** What the UI needs to know up front (GET /me). */
export interface Capabilities {
  /** Type media: upload, tag and organize in every album. */
  media: boolean;
  release: boolean;
  restricted: boolean;
  admin: boolean;
  /** Studio link: media, access to at least one album, or managing album access (admins). */
  studio: boolean;
  /** Create share links (released photos only). */
  share: boolean;
}

type Viewable = Pick<Photo, 'visibility' | 'status'>;

/**
 * Resolved permissions for one signed-in user. Pure: no I/O, so the API and the web app share it.
 *
 * - Type media: Studio with every album; upload, tag and ★ Pick anywhere; move/add between albums, Trash, restrict,
 *   removal review, Add to Glean, create albums and invite links. Can release (media only) adds release/unrelease.
 * - Type user: no Studio, except albums they were given access to (invite link or admin grant; stored as
 *   collaborators): there they see the album's photos, upload, tag and Pick.
 * - Sees restricted photos: restricted photos in Browse and Studio (marking restricted also needs media).
 * - Admin: the Admin area, and managing album access in Studio (only that part of Studio unless media or given albums).
 * - Browse (`/`) shows released photos, plus restricted ones to people who may see them. Never unreleased.
 * - Share links: media, released photos only. Flock posters edit tags on and delete their own posts.
 * Pending/suspended users can do nothing.
 */
export class Access {
  private readonly collabs: ReadonlySet<string>;

  constructor(
    readonly user: AccessUser,
    collaboratorAlbumIds: readonly string[] = [],
  ) {
    this.collabs = new Set(user.status === 'active' ? collaboratorAlbumIds : []);
  }

  get active(): boolean {
    return this.user.status === 'active';
  }

  get isAdmin(): boolean {
    return this.active && this.user.isAdmin;
  }

  /** Upload, tag and organize everywhere; every album in Studio. */
  get isMedia(): boolean {
    return this.active && this.user.type === 'media';
  }

  /** Release / unrelease: media with Can release (guards stale records of type user). */
  get canRelease(): boolean {
    return this.isMedia && this.user.canRelease;
  }

  get seesRestricted(): boolean {
    return this.active && this.user.canSeeRestricted;
  }

  /** Albums this user was given access to (invite link or admin grant). */
  get collaboratorAlbumIds(): string[] {
    return [...this.collabs];
  }

  isCollaborator(albumId: string | undefined): boolean {
    return !!albumId && this.collabs.has(albumId);
  }

  /** Studio content (albums and photos): media, or access to at least one album. */
  get hasStudio(): boolean {
    return this.isMedia || this.collabs.size > 0;
  }

  /** Give and take away album access (Studio → Album access, album People panels): media and admins. */
  get canManageAccess(): boolean {
    return this.isMedia || this.isAdmin;
  }

  /** The Studio area at all: its content, or only the Album access view (admins who aren't media). */
  get canOpenStudio(): boolean {
    return this.hasStudio || this.canManageAccess;
  }

  canOpenAlbum(albumId: string): boolean {
    return this.isMedia || this.isCollaborator(albumId);
  }

  canUploadTo(albumId: string): boolean {
    return this.canOpenAlbum(albumId);
  }

  /** Visibilities offered for new uploads into an album: never released; restricted for people who may see it. */
  uploadVisibilities(albumId: string): Visibility[] {
    if (!this.canUploadTo(albumId)) return [];
    return this.seesRestricted ? ['unreleased', 'restricted'] : ['unreleased'];
  }

  /* ---------- Browse ---------- */

  /** What Browse may show this user: released, plus restricted for people who may see it. Never unreleased. */
  get browseVisibilities(): Visibility[] {
    if (!this.active) return [];
    return this.seesRestricted ? ['released', 'restricted'] : ['released'];
  }

  canBrowse(photo: Viewable): boolean {
    return photo.status !== 'trashed' && this.browseVisibilities.includes(photo.visibility);
  }

  /* ---------- Studio ---------- */

  /** Visibilities this user can see in Studio at all (users with album access only within those albums). */
  get studioVisibilities(): Visibility[] {
    if (!this.hasStudio) return [];
    return this.seesRestricted ? ['unreleased', 'released', 'restricted'] : ['unreleased', 'released'];
  }

  /** A photo in Studio, given the albums it belongs to (home + references). */
  canSeeInStudio(photo: Viewable, albumIds: readonly string[]): boolean {
    if (photo.status === 'trashed' || !this.studioVisibilities.includes(photo.visibility)) return false;
    return this.isMedia || albumIds.some((a) => this.collabs.has(a));
  }

  /** Edit tags and ★ Pick: media, or access to one of the photo's albums. */
  canTagIn(albumIds: readonly string[]): boolean {
    return this.isMedia || albumIds.some((a) => this.collabs.has(a));
  }

  /** Mark restricted / unrestrict: media who see restricted photos. */
  get canRestrict(): boolean {
    return this.isMedia && this.seesRestricted;
  }

  /**
   * Whether this user may change a photo's visibility from `from` to `to` (a no-op only needs to see it). Into
   * restricted (from anywhere) needs `canRestrict`; out of restricted too, plus Can release when the target is
   * released; unreleased ⇄ released needs Can release.
   */
  canSetVisibility(from: Visibility, to: Visibility): boolean {
    if (!this.active) return false;
    if (from === to) return from !== 'restricted' || this.seesRestricted;
    if (to === 'restricted') return this.canRestrict;
    if (from === 'restricted') return this.canRestrict && (to !== 'released' || this.canRelease);
    return this.canRelease;
  }

  /** Grid cards carry no status (they're only ever ready photos). */
  canShare(photo: Pick<Photo, 'visibility'> & { status?: Photo['status'] }): boolean {
    return this.isMedia && photo.status !== 'trashed' && photo.visibility === 'released';
  }

  /** Flock posters edit tags on, and delete, their own posts. */
  canEditFlockPost(photo: Pick<Photo, 'source' | 'uploaderId'>): boolean {
    return this.active && photo.source === 'flock' && photo.uploaderId === this.user.id;
  }

  get capabilities(): Capabilities {
    return {
      media: this.isMedia,
      release: this.canRelease,
      restricted: this.seesRestricted,
      admin: this.isAdmin,
      studio: this.canOpenStudio,
      share: this.isMedia,
    };
  }
}

/** Can release only applies to media: a patch that leaves (or makes) someone a user turns it off. */
type Switches = Pick<User, 'type' | 'canRelease'>;
export function coupleSwitches<T extends object>(current: Switches, patch: T): T & Partial<Switches> {
  const out: T & Partial<Switches> = { ...patch };
  if ((out.type ?? current.type) === 'user' && (out.canRelease !== undefined || current.canRelease)) out.canRelease = false;
  return out;
}
